/**
 * The editor's local model and the change set it sends. Pure: no React, no network.
 *
 * Every edit in `TourEditor` is local until Save draft, so this file owns the two questions the
 * component would otherwise answer inline - "what would the next write send?" and "is there
 * anything unsaved?" - and a test can ask them without rendering anything.
 */
import type {
  AdminTourDetail,
  AdminTourNewStep,
  AdminTourPlatformKey,
  AdminTourStep,
  AdminTourStepChange,
} from '../../../types/adminTours';
import { PLATFORM_KEYS, PLATFORM_VALUES } from '../../../types/adminTours';

/** The em dash. Banned from user-facing copy: it is the tell that a machine wrote the sentence. */
export const EM_DASH = '—';

export const hasEmDash = (value: string): boolean => value.includes(EM_DASH);

export interface DraftState {
  tourEnabled: boolean;
  steps: Record<AdminTourPlatformKey, AdminTourStep[]>;
  /** Published admin steps the next publish will delete. */
  deletedKeys: string[];
}

export const toDraftState = (detail: AdminTourDetail): DraftState => ({
  tourEnabled: detail.isEnabled,
  steps: { web: [...detail.steps.web], mobile: [...detail.steps.mobile] },
  deletedKeys: [...detail.deletedStepKeys],
});

/**
 * The key an unpublished step carries, both locally and as the API hands it back.
 *
 * A step the admin has just added has no server key yet, so it is identified by the client id it
 * was created with. Writing that id into the key means a saved draft re-renders from the server
 * with the same key it had a moment ago: the row keeps its editing state, its position, and any
 * validation message attached to it.
 */
export const DRAFT_KEY_PREFIX = 'draft:';

export const isUnpublishedStep = (step: AdminTourStep): boolean =>
  step.key.startsWith(DRAFT_KEY_PREFIX);

/** Whether an admin may delete this step. Code steps are the manifest's, and are never deleted. */
export const canDeleteStep = (step: AdminTourStep): boolean =>
  step.source === 'Admin' && !step.removedFromCode;

const clientIdOf = (key: string): string => key.slice(DRAFT_KEY_PREFIX.length);

/** `crypto.randomUUID` where it exists, which is every browser this app supports and jsdom. */
const newClientId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export interface NewStepInput {
  platform: AdminTourPlatformKey;
  anchorId: string | null;
  anchorLabel: string | null;
}

/**
 * A step the admin is adding, in the shape the list already renders.
 *
 * Its default copy is empty rather than a copy of the title, so `isOverridden` stays false and the
 * form does not offer to revert a step that has nothing to revert to.
 */
export const newAdminStep = ({ platform, anchorId, anchorLabel }: NewStepInput): AdminTourStep => ({
  key: `${DRAFT_KEY_PREFIX}${newClientId()}`,
  platform: PLATFORM_VALUES[platform],
  anchorId,
  anchorLabel,
  source: 'Admin',
  title: '',
  body: '',
  defaultTitle: '',
  defaultBody: '',
  hasOverride: false,
  requiredPermission: null,
  // Never read locally, and never sent as this value: `buildStepWrite` numbers every step by its
  // position in the list the screen shows, which is the only order that can be trusted once steps
  // have been dragged around. Present because the row shape demands it.
  sortOrder: 0,
  isEnabled: true,
  isNewFromCode: false,
  removedFromCode: false,
});

/**
 * Whether the copy on a step differs from the code default. Derived rather than stored: an admin
 * who types a title and then types the default back has no override, which is also what the API
 * will conclude, and the two must not disagree about whether "Revert to default copy" belongs.
 */
export const isOverridden = (step: AdminTourStep): boolean =>
  step.title !== step.defaultTitle || step.body !== step.defaultBody;

export const withCopy = (step: AdminTourStep, title: string, body: string): AdminTourStep => {
  const next = { ...step, title, body };
  return { ...next, hasOverride: isOverridden(next) };
};

export const revertedToDefault = (step: AdminTourStep): AdminTourStep => ({
  ...step,
  title: step.defaultTitle,
  body: step.defaultBody,
  hasOverride: false,
});

/** Moves a step one place, staying inside the list. Returns the same array when it cannot move. */
export const movedStep = (
  steps: AdminTourStep[],
  index: number,
  delta: -1 | 1
): AdminTourStep[] => {
  const target = index + delta;
  if (index < 0 || index >= steps.length || target < 0 || target >= steps.length) return steps;
  const next = [...steps];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
};

/** Drag-and-drop's reorder: the dragged step lands where the step it was dropped on sits. */
export const reorderedSteps = (
  steps: AdminTourStep[],
  activeKey: string,
  overKey: string
): AdminTourStep[] => {
  const from = steps.findIndex((step) => step.key === activeKey);
  const to = steps.findIndex((step) => step.key === overKey);
  if (from < 0 || to < 0 || from === to) return steps;
  const next = [...steps];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export interface StepWrite {
  changes: Record<string, AdminTourStepChange>;
  newSteps: AdminTourNewStep[];
}

/**
 * The change set for the next write: every step the admin can edit, in the state the screen shows.
 *
 * Deliberately not a delta. `PUT {key}/draft` **replaces** the stored change set, and `GET {key}`
 * hands back steps with the existing draft already applied, so sending only what changed since the
 * load would drop every edit a previous session left in that draft: the new document would name one
 * step, and the rest of yesterday's draft would be gone with nothing on screen to say so.
 *
 * Steps removed from code are left out entirely. They are read-only, and the API refuses a write
 * that names one, which would otherwise turn a reorder of the steps around it into a failed save.
 */
export const buildStepWrite = (draft: DraftState): StepWrite => {
  const changes: Record<string, AdminTourStepChange> = {};
  const newSteps: AdminTourNewStep[] = [];

  PLATFORM_KEYS.forEach((platform) => {
    // Indexed over the steps that are actually sent, so the numbers stay 0..n-1. Indexing over the
    // displayed list would leave gaps where a removed step sits, and a removed step keeps whatever
    // sort order it was stored with, so the two schemes would only line up by luck. Published and
    // unpublished steps share the counter, because they share one running order on the screen.
    draft.steps[platform]
      .filter((step) => !step.removedFromCode)
      .forEach((step, index) => {
        if (isUnpublishedStep(step)) {
          newSteps.push({
            clientId: clientIdOf(step.key),
            platform: PLATFORM_VALUES[platform],
            anchorId: step.anchorId,
            title: step.title,
            body: step.body,
            sortOrder: index,
          });
          return;
        }
        changes[step.key] = {
          isEnabled: step.isEnabled,
          sortOrder: index,
          // Explicit nulls per field, never a single "clear both" flag: a step
          // with an overridden title and a default body has to be expressible, or reverting one of
          // the two silently leaves the published override in place behind a screen that shows the
          // default.
          titleOverride: step.title === step.defaultTitle ? null : step.title,
          bodyOverride: step.body === step.defaultBody ? null : step.body,
        };
      });
  });

  return { changes, newSteps };
};

/** What a write would send, as a value two renders can be compared by. */
export const draftPayload = (draft: DraftState) => {
  const { changes, newSteps } = buildStepWrite(draft);
  return JSON.stringify({
    tourEnabled: draft.tourEnabled,
    steps: changes,
    newSteps,
    deletedStepKeys: draft.deletedKeys,
  });
};
